'use client';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, MapPin, Search as SearchIcon, X } from 'lucide-react';
import { usePlanningSearch } from '../state/PlanningSearchContext';
import type { SearchSuggestion } from '../../../lib/search/searchUtils';
import { useBufferedSearchInput } from './useBufferedSearchInput';
import {
  getPlanningAiSearchPlanLabels,
  hasApplicablePlanningAiSearchPlan,
  isDirectPlanningSearchQuery,
  parsePlanningImpactSearchIntent,
  type PlanningAiSearchPlan,
} from '../../../lib/planning/aiSearch';
import { usePlanningAiAvailability } from '../../../lib/planning/aiAvailability';
import { buildPlanningSemanticTagUrl } from '../../../lib/planning/tags';
import {
  countBucket,
  textLengthBucket,
  trackPlanningUpdatesEvent,
} from '../../../lib/planningUpdatesAnalytics';

const KEY_CODES = {
  ENTER: 'Enter',
  ESC: 'Escape',
  ARROW_UP: 'ArrowUp',
  ARROW_DOWN: 'ArrowDown',
  BACKSPACE: 'Backspace'
};

const FREE_TEXT_ID = '__free-text__';

const buildDisplaySuggestions = (input: string, base: SearchSuggestion[]): SearchSuggestion[] => {
  const trimmed = input.trim();
  if (!trimmed) return base;

  const freeTextSuggestion: SearchSuggestion = {
    id: FREE_TEXT_ID,
    label: trimmed,
    type: 'keyword',
    subtitle: 'Search this text',
    score: 1
  };

  return [freeTextSuggestion, ...base];
};

interface GlobalSearchBarProps {
  variant?: 'default' | 'compact';
  className?: string;
}

const GlobalSearchBar: React.FC<GlobalSearchBarProps> = ({ variant = 'default', className }) => {
  const {
    searchInput,
    setSearchInput,
    searchTokens,
    applyDeterministicSearchIntent,
    addToken,
    removeToken,
    suggestions,
    selectSuggestion,
    facetCounts,
    setTags,
    setFacetState,
    setDatePreset,
    setRadius,
    requestGeocode,
    pendingGeocode,
    geocodeError
  } = usePlanningSearch();
  const {
    draftInput,
    updateDraftInput,
    cancelPendingSync,
  } = useBufferedSearchInput(searchInput, setSearchInput);

  const [isFocused, setIsFocused] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiStatus, setAiStatus] = useState<{ tone: 'success' | 'fallback'; message: string } | null>(null);
  const { status: aiAvailability, markAvailable: markAiAvailable, markUnavailable: markAiUnavailable } = usePlanningAiAvailability();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const availableTagsRef = useRef<Array<{ slug: string; label: string; count: number }>>([]);
  const listboxId = React.useId();
  const focusInput = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  const displaySuggestions = useMemo(
    () => buildDisplaySuggestions(draftInput, suggestions),
    [draftInput, suggestions]
  );

  const showSuggestions = isFocused && displaySuggestions.length > 0;

  useEffect(() => {
    const availableTags = (facetCounts.tags || []).slice(0, 800).map((tag) => ({
      slug: tag.value,
      label: tag.label,
      count: tag.count,
    }));
    if (availableTags.length > 0) availableTagsRef.current = availableTags;
  }, [facetCounts.tags]);

  const handleTokenRemoval = useCallback((token: string) => {
    removeToken(token);
    inputRef.current?.focus();
  }, [removeToken]);

  const commitInputAsToken = useCallback(() => {
    const trimmed = draftInput.trim();
    if (!trimmed) return;
    updateDraftInput('');
    cancelPendingSync();
    setSearchInput('');
    addToken(trimmed);
    setIsFocused(false);
  }, [addToken, cancelPendingSync, draftInput, setSearchInput, updateDraftInput]);

  const applySemanticTags = useCallback((
    tagSlugs: string[],
    impact?: { min: number | null; max: number | null },
  ) => {
    if (tagSlugs.length === 0) return;
    if (typeof window !== 'undefined') {
      const nextUrl = buildPlanningSemanticTagUrl(window.location.href, tagSlugs);
      if (nextUrl) {
        const url = new URL(nextUrl);
        if (impact?.min !== null && impact?.min !== undefined) {
          url.searchParams.set('impact', `${impact.min}-${impact.max ?? 10}`);
        }
        window.history.replaceState(window.history.state, '', url.toString());
      }
    }
    setTags(tagSlugs);
  }, [setTags]);

  const runUnifiedSearch = useCallback(async (interpretWithAi = false) => {
    const originalQuery = draftInput.trim();
    if (!originalQuery || aiLoading) return;

    if (isDirectPlanningSearchQuery(originalQuery)) {
      setAiStatus(null);
      commitInputAsToken();
      return;
    }

    cancelPendingSync();
    const deterministic = applyDeterministicSearchIntent(originalQuery);
    const impactIntent = parsePlanningImpactSearchIntent(deterministic.residualQuery);
    const query = impactIntent.residualQuery.trim();
    const applyImpactIntent = () => {
      if (impactIntent.impactMin === null && impactIntent.impactMax === null) return;
      setFacetState((previous) => ({
        ...previous,
        impactMin: impactIntent.impactMin,
        impactMax: impactIntent.impactMax,
      }));
    };
    updateDraftInput('');
    setSearchInput('');

    if (!query) {
      applyImpactIntent();
      setAiStatus({
        tone: 'success',
        message: deterministic.regionApplied
          ? `${impactIntent.label ? `${impactIntent.label} and area` : 'Area'} applied. You can refine the results with filters below.`
          : impactIntent.label
            ? `${impactIntent.label} applied.`
          : 'Search filters applied.',
      });
      return;
    }

    if (!interpretWithAi) {
      applyImpactIntent();
      addToken(query);
      setAiStatus(null);
      setIsFocused(false);
      return;
    }

    const availableTags = availableTagsRef.current;
    const applyTextFallback = (message: string) => {
      applyImpactIntent();
      addToken(query);
      setAiStatus({ tone: 'fallback', message });
    };
    if (aiAvailability === 'unavailable') {
      applyTextFallback('AI search is temporarily unavailable. Showing text-search matches.');
      return;
    }
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 8000);
    setAiLoading(true);
    setAiStatus(null);
    trackPlanningUpdatesEvent('planning_updates_ai_search_submitted', {
      placement: 'global_search',
      status: textLengthBucket(query),
      metadata: {
        input_length_bucket: textLengthBucket(query),
        available_tag_bucket: countBucket(availableTags.length),
        region_applied: deterministic.regionApplied,
        short_lets_applied: deterministic.shortLetsApplied,
      },
    });

    try {
      const response = await fetch('/api/planning-tags/suggest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, max_results: 8, available_tags: availableTags }),
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`AI search returned ${response.status}`);
      const plan = await response.json() as PlanningAiSearchPlan;
      if (plan.source !== 'ai') {
        markAiUnavailable();
        applyTextFallback('AI search is temporarily unavailable. Showing text-search matches.');
        return;
      }
      markAiAvailable();
      if (!hasApplicablePlanningAiSearchPlan(plan)) {
        applyTextFallback('No reliable AI filters were found. Showing text-search matches.');
        trackPlanningUpdatesEvent('planning_updates_ai_search_completed', {
          placement: 'global_search',
          status: 'text_fallback',
          metadata: { input_length_bucket: textLengthBucket(query), match_count: 0 },
        });
        return;
      }

      const tagSlugs = (plan.matches || []).map((match) => match.slug).filter(Boolean);
      if (plan.location_query) {
        await requestGeocode(plan.location_query);
        if (plan.radius_km != null) setRadius(plan.radius_km);
      }
      if (tagSlugs.length > 0) {
        applySemanticTags(tagSlugs, { min: impactIntent.impactMin, max: impactIntent.impactMax });
      }
      if (plan.statuses?.length) {
        setFacetState((previous) => ({ ...previous, statuses: Array.from(new Set(plan.statuses)) }));
      }
      // Council routing and semantic tags can replace URL/state while this request is in
      // flight, so apply the deterministic scale intent after those updates have landed.
      applyImpactIntent();
      if (plan.date_preset) setDatePreset(plan.date_preset);
      if (plan.residual_query) addToken(plan.residual_query);

      const labels = [
        ...(impactIntent.label ? [impactIntent.label] : []),
        ...getPlanningAiSearchPlanLabels(plan),
      ];
      const source = plan.source === 'ai' ? 'ai' : 'local_fallback';
      setAiStatus({
        tone: plan.source === 'ai' ? 'success' : 'fallback',
        message: labels.length > 0
          ? `Understood as: ${labels.join(' · ')}.`
          : 'Search filters applied.',
      });
      trackPlanningUpdatesEvent('planning_updates_ai_search_completed', {
        placement: 'global_search',
        status: source,
        value: tagSlugs.length,
        metadata: {
          input_length_bucket: textLengthBucket(query),
          match_count: tagSlugs.length,
          match_count_bucket: countBucket(tagSlugs.length),
          has_location: Boolean(plan.location_query),
          has_status: Boolean(plan.statuses?.length),
          has_date: Boolean(plan.date_preset),
          has_radius: plan.radius_km != null,
          has_residual_query: Boolean(plan.residual_query),
        },
      });
    } catch (error) {
      markAiUnavailable();
      const timedOut = error instanceof DOMException && error.name === 'AbortError';
      applyTextFallback(timedOut
        ? 'AI interpretation took too long. Showing text-search matches.'
        : 'AI interpretation was unavailable. Showing text-search matches.');
      trackPlanningUpdatesEvent('planning_updates_ai_search_failed', {
        placement: 'global_search',
        status: timedOut ? 'timeout' : 'unavailable',
        metadata: { input_length_bucket: textLengthBucket(query) },
      });
    } finally {
      window.clearTimeout(timeoutId);
      setAiLoading(false);
    }
  }, [
    addToken,
    aiAvailability,
    applySemanticTags,
    aiLoading,
    applyDeterministicSearchIntent,
    cancelPendingSync,
    commitInputAsToken,
    draftInput,
    markAiAvailable,
    markAiUnavailable,
    requestGeocode,
    setDatePreset,
    setFacetState,
    setRadius,
    setSearchInput,
    setTags,
    updateDraftInput,
  ]);

  const handleInputKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case KEY_CODES.ENTER: {
        event.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < displaySuggestions.length) {
          const selected = displaySuggestions[highlightedIndex];
          if (selected.id === FREE_TEXT_ID) {
            void runUnifiedSearch();
          } else {
            selectSuggestion(selected);
            setHighlightedIndex(-1);
          }
        } else {
          void runUnifiedSearch();
        }
        break;
      }
      case KEY_CODES.ARROW_DOWN: {
        event.preventDefault();
        setHighlightedIndex(prev => {
          const next = Math.min(prev + 1, displaySuggestions.length - 1);
          return next;
        });
        break;
      }
      case KEY_CODES.ARROW_UP: {
        event.preventDefault();
        setHighlightedIndex(prev => {
          const next = prev <= 0 ? displaySuggestions.length - 1 : prev - 1;
          return next;
        });
        break;
      }
      case KEY_CODES.ESC: {
        setIsFocused(false);
        setHighlightedIndex(-1);
        break;
      }
      case KEY_CODES.BACKSPACE: {
        if (!draftInput && searchTokens.length > 0) {
          handleTokenRemoval(searchTokens[searchTokens.length - 1]);
        }
        break;
      }
      default:
        break;
    }
  }, [displaySuggestions, draftInput, handleTokenRemoval, highlightedIndex, runUnifiedSearch, searchTokens, selectSuggestion]);

  useEffect(() => {
    if (!showSuggestions) {
      setHighlightedIndex(-1);
    }
  }, [showSuggestions]);

  const wrapperClasses = variant === 'compact'
    ? `relative w-full text-sm ${className ?? ''}`
    : `relative w-full ${className ?? ''}`;

  const suggestionListClasses = variant === 'compact'
    ? 'absolute z-20 mt-1 w-full overflow-hidden border border-[var(--c-border)] bg-[var(--c-surface)] shadow-[0_8px_24px_rgba(0,0,0,0.08)] text-sm'
    : 'absolute z-20 mt-1 w-full overflow-hidden border border-[var(--c-border)] bg-[var(--c-surface)] shadow-[0_8px_24px_rgba(0,0,0,0.08)]';

  const hasTypedQuery = draftInput.trim().length > 0;
  const usesAiSearch = hasTypedQuery && !isDirectPlanningSearchQuery(draftInput);
  const showAiAffordance = usesAiSearch && aiAvailability === 'available';

  return (
    <div className={wrapperClasses}>
      <div
        className={`pr-search cursor-text ${isFocused ? 'ring-0' : ''}`}
        role="combobox"
        aria-expanded={showSuggestions}
        aria-owns={listboxId}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        onMouseDown={(event) => {
          const target = event.target as HTMLElement;
          if (target.closest('button') || target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
            return;
          }
          event.preventDefault();
          focusInput();
        }}
      >
        <span className="pr-search-label">Search updates</span>
        <div className={`pr-search-body ${variant === 'compact' ? 'py-2' : ''}`}>
          <SearchIcon
            className={variant === 'compact' ? 'h-4 w-4 shrink-0 text-[var(--c-accent)] pointer-events-none' : 'h-5 w-5 shrink-0 text-[var(--c-accent)] pointer-events-none'}
            aria-hidden
          />
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 cursor-text">
            {searchTokens.map(token => (
              <button
                key={token}
                type="button"
                className={`pr-chip pr-chip--on cursor-pointer ${variant === 'compact' ? '!text-xs !py-1 !px-2' : ''}`}
                onClick={() => handleTokenRemoval(token)}
                aria-label={`Remove token ${token}`}
              >
                <span>{token}</span>
                <X className="h-3 w-3 opacity-70" aria-hidden />
              </button>
            ))}
            <input
              ref={inputRef}
              type="text"
              role="searchbox"
              aria-label="Search planning applications"
              inputMode="search"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              value={draftInput}
              onChange={(event) => {
                updateDraftInput(event.target.value);
                setAiStatus(null);
              }}
              onFocus={() => setIsFocused(true)}
              onBlur={() => {
                window.setTimeout(() => setIsFocused(false), 150);
              }}
              onKeyDown={handleInputKeyDown}
              placeholder={searchTokens.length === 0
                ? 'Address, postcode, reference or keywords…'
                : 'Add another term'}
              className={`min-w-[140px] flex-1 bg-transparent font-['Newsreader',Georgia,serif] text-[17px] leading-none text-[var(--c-text-primary)] placeholder:text-[var(--c-text-tertiary)] placeholder:font-['Inter',sans-serif] placeholder:text-sm placeholder:font-normal outline-none cursor-text appearance-none sm:min-w-[200px] ${variant === 'compact' ? 'text-base' : ''}`}
              aria-autocomplete="list"
              aria-controls={listboxId}
              data-global-search-input="true"
            />
          </div>
          {(hasTypedQuery || searchTokens.length > 0) && (
            <button type="button" aria-label="Clear search text" className="p-2 text-[var(--c-text-secondary)]"
              onClick={() => {
                cancelPendingSync();
                searchTokens.forEach(removeToken);
                updateDraftInput('');
                setSearchInput('');
                setAiStatus(null);
                focusInput();
              }}><X className="h-4 w-4" aria-hidden /></button>
          )}
          {showAiAffordance && (
            <button type="button" className="pr-textlink shrink-0 text-xs"
              onClick={() => void runUnifiedSearch(true)} disabled={aiLoading}>
              Interpret with AI
            </button>
          )}
          {hasTypedQuery && (
            <button
              type="button"
              className="pr-button shrink-0 !py-2 !px-3"
              onClick={() => void runUnifiedSearch()}
              disabled={aiLoading}
            >
              {aiLoading
                ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                : <SearchIcon className="h-4 w-4" aria-hidden />}
              {aiLoading ? 'Understanding' : 'Search'}
            </button>
          )}
        </div>
      </div>

      {showSuggestions && (
        <ul
          id={listboxId}
          role="listbox"
          className={suggestionListClasses}
        >
          {displaySuggestions.map((suggestion, index) => {
            const isActive = index === highlightedIndex;
            const isStreet = suggestion.accuracy === 'street'
              || !!suggestion.streetKey
              || (suggestion.subtitle && suggestion.subtitle.toLowerCase().includes('street'));
            const isKeyword = suggestion.type === 'keyword'
              || (suggestion.subtitle && (suggestion.subtitle.toLowerCase().includes('keyword') || suggestion.subtitle.toLowerCase().includes('term')));
            const isFreeText = suggestion.id === FREE_TEXT_ID;
            return (
              <li key={suggestion.id} role="option" aria-selected={isActive}>
                <button
                  type="button"
                  className={`flex w-full items-center justify-between gap-3 border-b border-[var(--c-border)] px-4 py-2.5 text-left text-sm transition-colors last:border-b-0 ${
                    isActive
                      ? 'bg-[var(--c-surface-hover)] text-[var(--c-text-primary)]'
                      : 'text-[var(--c-text-secondary)] hover:bg-[var(--c-surface-hover)]'
                  } ${
                    isFreeText || isStreet || isKeyword
                      ? 'border-l-2 border-l-[var(--c-accent)]'
                      : ''
                  }`}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    if (suggestion.id === FREE_TEXT_ID) {
                      void runUnifiedSearch();
                    } else {
                      selectSuggestion(suggestion);
                      setHighlightedIndex(-1);
                    }
                  }}
                >
                  <span className="flex items-center gap-2">
                    {suggestion.type === 'address' && !isFreeText && <MapPin className="h-4 w-4 opacity-70" aria-hidden />}
                    <span className="font-medium text-[var(--c-text-primary)]">{suggestion.label}</span>
                    {suggestion.subtitle && (
                      <span className="font-['DM_Mono'] text-[10px] uppercase tracking-[0.08em] text-[var(--c-text-tertiary)]">
                        {suggestion.subtitle}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {pendingGeocode && (
        <div className={`text-[var(--c-text-secondary)] flex items-center gap-2 ${variant === 'compact' ? 'mt-1 text-xs' : 'mt-2 text-sm'}`}>
          <span className="inline-flex h-3 w-3 animate-spin rounded-full border-2 border-[var(--c-accent-muted)] border-t-transparent" />
          Geocoding location...
        </div>
      )}

      {aiStatus && (
        <div
          className={`mt-2 border-l-2 py-1 pl-3 text-xs ${aiStatus.tone === 'success' ? 'border-[var(--c-accent)] text-[var(--c-text-secondary)]' : 'border-[var(--c-warning)] text-[var(--c-text-secondary)]'}`}
          role="status"
          aria-live="polite"
        >
          {aiStatus.message} <span className="whitespace-nowrap">Edit or remove the filter chips below.</span>
        </div>
      )}

      {geocodeError && (
        <div className={`border-l-2 border-[var(--c-error)] text-[var(--c-error)] ${variant === 'compact' ? 'mt-2 py-1 pl-2.5 text-xs' : 'mt-2 py-1 pl-3 text-sm'}`}>
          {geocodeError}
          <button
            type="button"
            className="ml-3 underline"
            onClick={() => requestGeocode(draftInput)}
          >
            Try again
          </button>
        </div>
      )}
    </div>
  );
};

export default GlobalSearchBar;
